import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Table,
  TextInput,
  Alert,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import { HiPencilAlt, HiTrash } from "react-icons/hi";
import type { ContractCategory } from "../../services/contract-categories";
import {
  createCategory,
  deleteCategory,
  getContractCategories,
  updateCategory,
} from "../../services/contract-categories";

interface CategoriesModalProps {
  show: boolean;
  onClose: () => void;
  onUpdate: () => Promise<void>;
}

const CategoriesModal: FC<CategoriesModalProps> = ({
  show,
  onClose,
  onUpdate,
}) => {
  const [categories, setCategories] = useState<ContractCategory[]>([]);
  const [showInactive, setShowInactive] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCategory, setNewCategory] = useState({
    category: "",
    active: true,
  });
  const [editingCategory, setEditingCategory] =
    useState<ContractCategory | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      const data = await getContractCategories();
      setCategories(data);
    };
    if (show) {
      loadCategories();
    }
  }, [show]);

  const validateCategory = (category: { category: string }) => {
    if (!category.category.trim()) {
      setError("Geben sie einen Kategorienamen ein");
      return false;
    }
    return true;
  };

  const handleAddCategory = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!validateCategory(newCategory)) return;

    setIsSubmitting(true);
    try {
      const result = await createCategory({
        category: newCategory.category.trim(),
        active: true,
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to create category");
      }

      setSuccessMessage("Category added successfully");
      await onUpdate();
      const updatedCategories = await getContractCategories();
      setCategories(updatedCategories);
      setIsAddingNew(false);
      setNewCategory({
        category: "",
        active: true,
      });
    } catch (error) {
      console.error("Error creating category:", error);
      setError(
        error instanceof Error ? error.message : "Failed to create category"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory) return;

    setError(null);
    setSuccessMessage(null);

    if (!validateCategory(editingCategory)) return;

    setIsSubmitting(true);
    try {
      const result = await updateCategory(editingCategory.id, {
        category: editingCategory.category.trim(),
        active: editingCategory.active,
      });

      if (!result.success) {
        throw new Error(
          result.error || "Fehler beim Aktualisieren der Kategorie."
        );
      }

      setSuccessMessage("Kategorie erfolgreich aktualisiert.");
      await onUpdate();
      const updatedCategories = await getContractCategories();
      setCategories(updatedCategories);
      setEditingCategory(null);
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Kategorie.", error);
      setError(
        error instanceof Error
          ? error.message
          : "Fehler beim Aktualisieren der Kategorie."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartEdit = (category: ContractCategory) => {
    setEditingCategory(category);
    setError(null);
    setSuccessMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setError(null);
    setSuccessMessage(null);
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      const result = await deleteCategory(id);
      if (result.success) {
        setSuccessMessage("Kategorie erfolgreich gelöscht.");
        await onUpdate();
        const updatedCategories = await getContractCategories();
        setCategories(updatedCategories);
      } else {
        setError(result.error || "Fehler beim Löschen der Kategorie.");
      }
    } catch (error) {
      console.error("Fehler beim Löschen der Kategorie.", error);
      setError(
        error instanceof Error
          ? error.message
          : "Fehler beim Löschen der Kategorie."
      );
    }
  };

  return (
    <Modal size="7xl" onClose={onClose} show={show}>
      <ModalHeader className="border-b border-gray-200 !p-8 dark:border-gray-700">
        <strong>Manage Categories</strong>
      </ModalHeader>
      <ModalBody className="max-h-[calc(100vh-200px)] overflow-y-auto p-8">
        {error && (
          <Alert color="failure" className="mb-4">
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert color="success" className="mb-4">
            {successMessage}
          </Alert>
        )}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <input
              id="show-inactive"
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
            />
            <label htmlFor="show-inactive">Show inactive categories</label>
          </div>
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
            <TableHead className="bg-gray-100 dark:bg-gray-700">
              <TableHeadCell>Kategorie</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Aktionen</TableHeadCell>
            </TableHead>
            <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {categories
                .filter((category) => showInactive || category.active)
                .map((category) =>
                  editingCategory?.id === category.id ? (
                    <TableRow
                      key={category.id}
                      className="hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <TableCell className="whitespace-nowrap p-6">
                        <TextInput
                          value={editingCategory.category}
                          onChange={(e) =>
                            setEditingCategory({
                              ...editingCategory,
                              category: e.target.value,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div
                          className="flex items-center gap-2"
                          role="group"
                          aria-label="Category Status"
                        >
                          <input
                            id={`active-${editingCategory.id}`}
                            type="checkbox"
                            checked={editingCategory.active}
                            onChange={(e) =>
                              setEditingCategory({
                                ...editingCategory,
                                active: e.target.checked,
                              })
                            }
                            className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600"
                          />
                          <label htmlFor={`active-${editingCategory.id}`}>
                            Aktiv
                          </label>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-x-3">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={handleEditCategory}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            color="gray"
                            size="sm"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow
                      key={category.id}
                      className="hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <TableCell className="whitespace-nowrap p-6 text-base font-medium text-gray-900 dark:text-white">
                        {category.category}
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-x-2">
                          <div
                            className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
                              category.active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {category.active ? "Active" : "Inactive"}
                          </div>
                          {!category.active && (
                            <span className="text-sm text-gray-500">
                              (Hidden from selection)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap p-6">
                        <div className="flex items-center gap-x-3">
                          <Button
                            color="primary"
                            size="sm"
                            onClick={() => handleStartEdit(category)}
                          >
                            <HiPencilAlt className="mr-2 text-lg" />
                            Bearbeiten
                          </Button>
                          <Button
                            color="failure"
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            <HiTrash className="mr-2 text-lg" />
                            Löschen
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                )}
              {isAddingNew && (
                <TableRow className="hover:bg-gray-100 dark:hover:bg-gray-700">
                  <TableCell className="whitespace-nowrap p-6">
                    <TextInput
                      value={newCategory.category}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          category: e.target.value,
                        })
                      }
                      placeholder="Geben sie einen Kategorienamen ein."
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <div className="inline-flex rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                      Aktiv
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap p-6">
                    <div className="flex items-center gap-x-3">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleAddCategory}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        color="gray"
                        size="sm"
                        onClick={() => setIsAddingNew(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!isAddingNew && (
            <div className="flex justify-end">
              <Button color="primary" onClick={() => setIsAddingNew(true)}>
                Kategorie hinzufügen
              </Button>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="p-8">
        <Button color="gray" onClick={onClose}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CategoriesModal;
